/**
 * Pancake Tower — canvas renderer. Pure drawing: reads a Scene, never mutates it.
 * All art is drawn here with paths (no images/emoji). Palette: cream #fff8ee,
 * pumpkin #df7836, deep pumpkin #b94716, espresso #3b2a20.
 */
import { CONFIG, Debris, GameState, Topping } from './logic';

export type Phase = 'start' | 'playing' | 'over';

export interface Sparkle {
  x: number; // world
  y: number; // world
  vx: number;
  vy: number;
  life: number; // ms left
  max: number;
  size: number; // css px
  color: string;
}

export interface Popup {
  text: string;
  x: number; // world
  y: number; // world
  life: number;
  max: number;
}

export interface Scene {
  game: GameState;
  debris: Debris[];
  sparkles: Sparkle[];
  popups: Popup[];
  /** world height shown at the bottom of the view (smoothed) */
  camera: number;
  /** css px amplitude, decays */
  shake: number;
  /** 0..1 landing squash of the top layer, decays */
  squash: number;
  /** 0..1 perfect glow ring, decays */
  flash: number;
  /** escaping ghost after a miss (world coords) */
  ghost: { x: number; y: number } | null;
  time: number;
}

export interface Viewport {
  cssW: number;
  cssH: number;
  dpr: number;
  /** css px per world unit */
  scale: number;
}

/** world units visible below the top of the plate */
export const BOTTOM_MARGIN = 60;

const C = {
  cream: '#fff8ee',
  pumpkin: '#df7836',
  deep: '#b94716',
  espresso: '#3b2a20',
};

const FONT = 'Nunito, "Noto Sans Thai", "Sarabun", "Leelawadee UI", Tahoma, system-ui, sans-serif';

const PANCAKE = { edge: '#c47a35', body: '#f1b762', shine: '#fcdca0' };
const PUMPKIN_CAKE = { edge: C.deep, body: C.pumpkin, shine: '#f8b27a' };

export function viewHeightWorld(vp: Viewport): number {
  return vp.cssH / vp.scale - BOTTOM_MARGIN;
}

function rr(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const rad = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + rad, y);
  ctx.arcTo(x + w, y, x + w, y + h, rad);
  ctx.arcTo(x + w, y + h, x, y + h, rad);
  ctx.arcTo(x, y + h, x, y, rad);
  ctx.arcTo(x, y, x + w, y, rad);
  ctx.closePath();
}

function drawPancake(
  ctx: CanvasRenderingContext2D,
  px: number, py: number, pw: number, ph: number,
  index: number, topping: Topping, isTop: boolean,
) {
  const pal = index % 6 === 5 ? PUMPKIN_CAKE : PANCAKE;
  const r = ph * 0.48;
  ctx.fillStyle = pal.edge;
  rr(ctx, px, py, pw, ph, r);
  ctx.fill();
  ctx.fillStyle = pal.body;
  rr(ctx, px, py, pw, ph * 0.74, r);
  ctx.fill();
  if (pw > r * 2) {
    ctx.fillStyle = pal.shine;
    rr(ctx, px + r * 0.7, py + ph * 0.16, pw - r * 1.4, ph * 0.13, ph * 0.07);
    ctx.fill();
  }

  if (topping === 'syrup') {
    ctx.fillStyle = '#7a3f17';
    rr(ctx, px + r * 0.3, py, Math.max(0, pw - r * 0.6), ph * 0.22, ph * 0.11);
    ctx.fill();
    const dripW = Math.min(ph * 0.42, pw * 0.3);
    for (let k = 0; k < 2; k++) {
      const dx = px + pw * (k === 0 ? 0.16 : 0.68);
      const len = ph * (0.62 + 0.3 * (((index * 37 + k * 13) % 5) / 4));
      rr(ctx, dx, py + ph * 0.05, dripW, len, dripW / 2);
      ctx.fill();
    }
  } else if (topping === 'berries' && pw > ph * 1.6) {
    for (const f of [0.3, 0.64]) {
      const bx = px + pw * f;
      const by = py + ph * 0.12;
      ctx.fillStyle = '#9b2c46';
      ctx.beginPath();
      ctx.arc(bx, by, ph * 0.22, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#f6c5cf';
      ctx.beginPath();
      ctx.arc(bx - ph * 0.07, by - ph * 0.07, ph * 0.06, 0, Math.PI * 2);
      ctx.fill();
    }
  } else if (topping === 'butter') {
    if (isTop && pw > ph * 1.4) {
      const bw = ph * 1.1;
      const cx = px + pw / 2;
      ctx.fillStyle = '#f2c75c';
      rr(ctx, cx - bw / 2, py - ph * 0.42, bw, ph * 0.56, ph * 0.12);
      ctx.fill();
      ctx.fillStyle = '#ffeeb5';
      rr(ctx, cx - bw / 2, py - ph * 0.42, bw, ph * 0.4, ph * 0.12);
      ctx.fill();
    } else {
      ctx.fillStyle = '#fff1cf';
      rr(ctx, px + r * 0.4, py - ph * 0.04, Math.max(0, pw - r * 0.8), ph * 0.16, ph * 0.08);
      ctx.fill();
    }
  }
}

/** Tiny original ghost with a pumpkin mug. (cx, by) = bottom centre in css px. */
function drawGhost(ctx: CanvasRenderingContext2D, cx: number, by: number, h: number, oops: boolean) {
  const w = h * 0.86;
  ctx.save();
  ctx.translate(cx, by);
  ctx.beginPath();
  ctx.moveTo(-w / 2, 0);
  ctx.lineTo(-w / 2, -h + w / 2);
  ctx.arc(0, -h + w / 2, w / 2, Math.PI, 0);
  ctx.lineTo(w / 2, 0);
  const scallop = w / 3;
  for (let i = 0; i < 3; i++) {
    const x0 = w / 2 - i * scallop;
    ctx.quadraticCurveTo(x0 - scallop / 2, -h * 0.16, x0 - scallop, 0);
  }
  ctx.closePath();
  ctx.fillStyle = '#fffaf0';
  ctx.fill();
  ctx.lineWidth = Math.max(1, h * 0.04);
  ctx.strokeStyle = '#dcc7a8';
  ctx.stroke();

  const eyeY = -h * 0.6;
  ctx.fillStyle = C.espresso;
  for (const ex of [-w * 0.18, w * 0.18]) {
    ctx.beginPath();
    ctx.ellipse(ex, eyeY, h * 0.045, h * 0.065, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = 'rgba(230,150,130,0.55)';
  for (const ex of [-w * 0.3, w * 0.3]) {
    ctx.beginPath();
    ctx.ellipse(ex, eyeY + h * 0.11, h * 0.07, h * 0.035, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.strokeStyle = C.espresso;
  ctx.lineWidth = Math.max(1, h * 0.04);
  ctx.beginPath();
  if (oops) ctx.arc(0, eyeY + h * 0.15, h * 0.05, 0, Math.PI * 2);
  else ctx.arc(0, eyeY + h * 0.07, h * 0.07, 0.15 * Math.PI, 0.85 * Math.PI);
  ctx.stroke();

  // pumpkin mug
  const mw = w * 0.34;
  const mx = w * 0.12;
  const my = -h * 0.36;
  ctx.strokeStyle = C.deep;
  ctx.lineWidth = Math.max(1, h * 0.05);
  ctx.beginPath();
  ctx.arc(mx + mw, my + mw * 0.45, mw * 0.28, -Math.PI / 2, Math.PI / 2);
  ctx.stroke();
  ctx.fillStyle = C.pumpkin;
  rr(ctx, mx, my, mw, mw * 0.95, mw * 0.25);
  ctx.fill();
  ctx.fillStyle = '#5a3a28';
  rr(ctx, mx + mw * 0.12, my + mw * 0.06, mw * 0.76, mw * 0.16, mw * 0.08);
  ctx.fill();
  ctx.restore();
}

function star(ctx: CanvasRenderingContext2D, x: number, y: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x, y - r);
  ctx.lineTo(x + r * 0.28, y - r * 0.28);
  ctx.lineTo(x + r, y);
  ctx.lineTo(x + r * 0.28, y + r * 0.28);
  ctx.lineTo(x, y + r);
  ctx.lineTo(x - r * 0.28, y + r * 0.28);
  ctx.lineTo(x - r, y);
  ctx.lineTo(x - r * 0.28, y - r * 0.28);
  ctx.closePath();
  ctx.fill();
}

export function drawScene(ctx: CanvasRenderingContext2D, sc: Scene, vp: Viewport, phase: Phase, reducedMotion: boolean) {
  const { cssW, cssH, scale: s } = vp;
  const H = CONFIG.layerHeight;
  const ph = H * s;
  const g = sc.game;
  const cam = sc.camera;
  const sy = (worldY: number) => cssH - (BOTTOM_MARGIN + worldY - cam) * s;

  ctx.setTransform(vp.dpr, 0, 0, vp.dpr, 0, 0);

  // background
  const bg = ctx.createLinearGradient(0, 0, 0, cssH);
  bg.addColorStop(0, C.cream);
  bg.addColorStop(1, '#fde4c8');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, cssW, cssH);

  // drifting soft dots (parallax with the camera)
  ctx.fillStyle = 'rgba(223,120,54,0.10)';
  for (let i = 0; i < 10; i++) {
    const x = (((i * 83 + 17) % 100) / 100) * cssW;
    const span = cssH + 40;
    const y = ((((i * 131) % 1000) / 1000) * span + cam * s * 0.35) % span - 20;
    ctx.beginPath();
    ctx.arc(x, y, 3 + (i % 3) * 2, 0, Math.PI * 2);
    ctx.fill();
  }

  const shake = reducedMotion ? 0 : sc.shake;
  ctx.save();
  if (shake > 0.2) ctx.translate(Math.sin(sc.time * 0.09) * shake, Math.cos(sc.time * 0.07) * shake * 0.5);

  // height markers every 10 layers
  ctx.font = `700 11px ${FONT}`;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'bottom';
  ctx.strokeStyle = 'rgba(59,42,32,0.16)';
  ctx.fillStyle = 'rgba(59,42,32,0.38)';
  ctx.lineWidth = 1;
  ctx.setLineDash([4, 6]);
  const firstMark = Math.max(10, Math.ceil((cam - 40) / H / 10) * 10);
  for (let n = firstMark; ; n += 10) {
    const y = sy(n * H);
    if (y < -20) break;
    if (y > cssH + 20) continue;
    ctx.beginPath();
    ctx.moveTo(cssW - 54, y);
    ctx.lineTo(cssW - 8, y);
    ctx.stroke();
    ctx.fillText(`${n} ชั้น`, cssW - 8, y - 2);
  }
  ctx.setLineDash([]);

  // table + plate
  const y0 = sy(0);
  if (y0 < cssH + ph * 2) {
    ctx.fillStyle = '#ecd0ae';
    ctx.fillRect(0, y0 + ph * 0.7, cssW, cssH);
    ctx.fillStyle = '#dcb489';
    ctx.fillRect(0, y0 + ph * 0.7, cssW, 2);
    const cx = (CONFIG.worldWidth / 2) * s;
    const pw = ((CONFIG.baseWidth + 70) * s) / 2;
    ctx.fillStyle = 'rgba(59,42,32,0.13)';
    ctx.beginPath();
    ctx.ellipse(cx, y0 + ph * 0.95, pw * 0.92, ph * 0.38, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#e6d6bf';
    ctx.beginPath();
    ctx.ellipse(cx, y0 + ph * 0.4, pw, ph * 0.58, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#fffdf8';
    ctx.beginPath();
    ctx.ellipse(cx, y0 + ph * 0.26, pw * 0.93, ph * 0.42, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  // stack (bottom-up so each layer sits over the previous one's topping)
  const topIndex = g.stack.length - 1;
  const firstVisible = Math.max(0, Math.floor((cam - BOTTOM_MARGIN - H * 2) / H));
  for (let i = firstVisible; i <= topIndex; i++) {
    const L = g.stack[i];
    let top = sy((i + 1) * H);
    if (top > cssH + ph) continue;
    let h = ph;
    if (i === topIndex && sc.squash > 0 && !reducedMotion) {
      const k = 1 - 0.22 * sc.squash;
      top += ph * (1 - k);
      h = ph * k;
    }
    drawPancake(ctx, L.x * s, top, L.w * s, h, i, L.topping, i === topIndex);
  }

  // perfect glow ring around the top layer
  if (sc.flash > 0 && topIndex >= 0) {
    const L = g.stack[topIndex];
    const grow = (1 - sc.flash) * (reducedMotion ? 4 : 18);
    ctx.strokeStyle = `rgba(255,236,170,${sc.flash})`;
    ctx.lineWidth = 3;
    rr(ctx, L.x * s - grow, sy((topIndex + 1) * H) - grow, L.w * s + grow * 2, ph + grow * 2, ph * 0.5 + grow);
    ctx.stroke();
  }

  // the sliding layer + its aim shadow + ghost rider
  const c = g.current;
  const rowY = g.stack.length * H;
  if (phase === 'playing' && !g.over) {
    const topL = g.stack[topIndex];
    const l = Math.max(c.x, topL.x);
    const r = Math.min(c.x + c.w, topL.x + topL.w);
    if (r > l) {
      ctx.fillStyle = 'rgba(59,42,32,0.12)';
      rr(ctx, l * s, sy(rowY), (r - l) * s, ph * 0.32, ph * 0.16);
      ctx.fill();
    }
    drawPancake(ctx, c.x * s, sy(rowY + H), c.w * s, ph, g.stack.length, 'plain', false);
    const bob = reducedMotion ? 0 : Math.sin(sc.time / 220) * ph * 0.08;
    drawGhost(ctx, (c.x + c.w / 2) * s, sy(rowY + H) + ph * 0.12 + bob, ph * 1.45, false);
  } else if (phase === 'start') {
    const topL = g.stack[topIndex];
    drawGhost(ctx, (topL.x + topL.w / 2) * s, sy(rowY) + ph * 0.12, ph * 1.45, false);
  }

  // falling trimmed pieces
  for (const d of sc.debris) {
    const top = sy(d.y + H);
    if (top > cssH + ph * 2) continue;
    const w = d.w * s;
    ctx.save();
    ctx.translate(d.x * s + w / 2, top + ph / 2);
    ctx.rotate(d.rot);
    drawPancake(ctx, -w / 2, -ph / 2, w, ph, 0, d.topping, false);
    ctx.restore();
  }

  if (sc.ghost) {
    drawGhost(ctx, sc.ghost.x * s, sy(sc.ghost.y), ph * 1.45, true);
  }

  // sparkles
  for (const p of sc.sparkles) {
    ctx.globalAlpha = Math.max(0, p.life / p.max);
    ctx.fillStyle = p.color;
    star(ctx, p.x * s, sy(p.y), p.size);
  }
  ctx.globalAlpha = 1;
  ctx.restore();

  // popups (not shaken)
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (const p of sc.popups) {
    const k = p.life / p.max;
    ctx.globalAlpha = Math.min(1, k * 1.6);
    ctx.font = `800 ${Math.round(20 + (1 - k) * 4)}px ${FONT}`;
    const y = sy(p.y) - (1 - k) * 28;
    ctx.lineWidth = 4;
    ctx.strokeStyle = C.cream;
    ctx.strokeText(p.text, p.x * s, y);
    ctx.fillStyle = C.deep;
    ctx.fillText(p.text, p.x * s, y);
  }
  ctx.globalAlpha = 1;

  // HUD
  if (phase === 'playing') {
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.font = `800 36px ${FONT}`;
    ctx.fillStyle = C.espresso;
    ctx.fillText(String(g.score), cssW / 2, 14);
    if (g.streak >= 2) {
      ctx.font = `800 14px ${FONT}`;
      ctx.fillStyle = C.deep;
      ctx.fillText(`เป๊ะต่อเนื่อง ×${g.streak}`, cssW / 2, 56);
    }
  }
}
